import { useEffect, useState } from 'react'
import { ArrowRight, ClipboardCheck } from 'lucide-react'
import { Link } from 'react-router-dom'

import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { CardContent, CardHeader, CardTitle, CardDescription } from './ui/card'
import { EmptyState } from './ui/empty-state'
import { Skeleton } from './ui/skeleton'
import { Button } from './ui/button'
import { rangeLabelAsOf } from '../lib/dayLabel'

interface MemberDashboardCardProps {
  teamId: string
  range: { from: string; to: string }
}

interface StandupRow {
  date: string
  questions: { yesterday?: string; today?: string; blockers?: string } | null
}

export function MemberDashboardCard({ teamId, range }: MemberDashboardCardProps) {
  const { user } = useAuth()
  const [standup, setStandup] = useState<StandupRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      if (!user?.id) return

      setLoading(true)
      setError(false)

      const { data: member, error: memberError } = await supabase
        .from('users')
        .select('id')
        .eq('team_id', teamId)
        .eq('auth_user_id', user.id)
        .maybeSingle()

      if (memberError || !member) {
        if (!cancelled) {
          setError(true)
          setLoading(false)
        }
        return
      }

      const { data, error: standupError } = await supabase
        .from('standup_responses')
        .select('date, questions')
        .eq('team_id', teamId)
        .eq('user_id', member.id)
        .gte('date', range.from)
        .lte('date', range.to)
        .order('date', { ascending: false })
        .limit(1)

      if (cancelled) return

      if (standupError) {
        setError(true)
      } else {
        setStandup((data?.[0] as StandupRow | undefined) ?? null)
      }
      setLoading(false)
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [teamId, range.from, range.to, user?.id])

  return (
    <div className="flex h-full flex-col">
      <CardHeader>
        <CardTitle className="text-base">Your standup</CardTitle>
        <CardDescription>Latest update {rangeLabelAsOf(range.from, range.to)}.</CardDescription>
      </CardHeader>
      <div className="border-t border-border" />
      <CardContent className="flex flex-1 flex-col pt-4">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
          </div>
        ) : error ? (
          <p className="text-sm text-destructive">Could not load your latest standup.</p>
        ) : standup ? (
          <div className="flex flex-1 flex-col gap-3">
            <p className="text-sm font-medium text-foreground">
              Submitted {new Date(`${standup.date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </p>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><span className="font-medium text-foreground">Completed:</span> {standup.questions?.yesterday || 'No update provided.'}</p>
              <p><span className="font-medium text-foreground">Next:</span> {standup.questions?.today || 'No update provided.'}</p>
              {standup.questions?.blockers && standup.questions.blockers !== 'None' && (
                <p><span className="font-medium text-foreground">Blocker:</span> {standup.questions.blockers}</p>
              )}
            </div>
            <Button asChild variant="outline" size="sm" className="mt-auto w-fit">
              <Link to={`/dashboard/team/${teamId}/standups/check-ins`}>
                View standups <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        ) : (
          <div className="flex flex-1 flex-col">
            <EmptyState icon={ClipboardCheck} message="You have not submitted a standup in this window." />
            <Button asChild variant="outline" size="sm" className="mx-auto mb-4">
              <Link to={`/dashboard/team/${teamId}/standups/check-ins`}>Open standups</Link>
            </Button>
          </div>
        )}
      </CardContent>
    </div>
  )
}
